/**
 * ReviewGate — Human Review Gate component (ticket-03, ADR-0002).
 *
 * Shows a modal panel before any AI changes are applied:
 *  - Text list of planned operations
 *  - Conflict warnings (ResourceClaim overlaps)
 *  - "Apply" button enabled only after user checks "ознакомлен" checkbox
 *
 * "Unit test of props" seam: strict TypeScript types ensure correctness at compile time.
 */

import { useState, useRef, useEffect } from 'react'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ReviewGateOperation {
  /** Human-readable description of what will be changed. */
  label: string
  /** Optional category for colour coding: install | remove | patch | unpatch | custom */
  kind?: 'install' | 'remove' | 'patch' | 'unpatch' | 'custom'
}

export interface ReviewGateProps {
  /** Ordered list of AI-planned operations. Required, must be non-empty. */
  operations: ReviewGateOperation[]
  /**
   * Conflict warnings from ResourceClaim validation.
   * If non-empty, a warning banner is shown — Apply is NOT blocked, only warned.
   */
  conflicts?: string[]
  /** Called when user confirms the review gate. */
  onConfirm: () => void
  /** Called when user cancels. */
  onCancel: () => void
  /** Shows a spinner on the Apply button while the action is in flight. */
  isLoading?: boolean
  /** Title shown in the modal header. */
  title?: string
  /** P4-1: optional visual preview (e.g. <RackDiffPreview/>) rendered above the operations list. */
  preview?: React.ReactNode
}

// ─── Kind badge colours ──────────────────────────────────────────────────────

const KIND_STYLES: Record<string, string> = {
  install: 'bg-green-500/15 text-green-400 border-green-500/30',
  remove: 'bg-red-500/15 text-red-400 border-red-500/30',
  patch: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  unpatch: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
  custom: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
}

const KIND_LABELS: Record<string, string> = {
  install: 'Установка',
  remove: 'Демонтаж',
  patch: 'Подключение',
  unpatch: 'Отключение',
  custom: 'Действие',
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * ReviewGate renders a blocking modal. The user must:
 *  1. Review the operation list (optionally scroll it)
 *  2. Check the "Ознакомлен" checkbox
 *
 * Only then does the Apply button become enabled.
 */
export function ReviewGate({
  operations,
  conflicts = [],
  onConfirm,
  onCancel,
  isLoading = false,
  title = 'Подтверждение изменений',
  preview,
}: ReviewGateProps) {
  const [acknowledged, setAcknowledged] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)

  // Scroll-to-bottom also counts as having reviewed the list
  // (bonus UX: auto-check after scrolling to end)
  const handleScroll = () => {
    const el = listRef.current
    if (!el) return
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4
    if (atBottom) setAcknowledged(true)
  }

  // Trap focus inside modal when open
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    return () => { prev?.focus() }
  }, [])

  const canApply = acknowledged && !isLoading

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="review-gate-title"
    >
      {/* Panel */}
      <div className="relative w-full max-w-lg rounded-lg border border-border bg-bg-panel shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2
            id="review-gate-title"
            className="text-base font-semibold text-text-primary"
          >
            {title}
          </h2>
          <button
            onClick={onCancel}
            className="text-text-secondary hover:text-text-primary transition-colors"
            aria-label="Закрыть"
          >
            ✕
          </button>
        </div>

        {/* Conflict warnings */}
        {conflicts.length > 0 && (
          <div className="mx-6 mt-4 rounded border border-yellow-500/40 bg-yellow-500/10 p-3">
            <p className="text-xs font-semibold text-yellow-400 mb-1">
              ⚠ Обнаружены конфликты ресурсов
            </p>
            <ul className="list-disc list-inside space-y-0.5">
              {conflicts.map((c, i) => (
                <li key={i} className="text-xs text-yellow-300/80">{c}</li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-yellow-300/60">
              Применение разрешено, но рекомендуется разрешить конфликты сначала.
            </p>
          </div>
        )}

        {/* P4-1: visual rack diff (before/after) */}
        {preview}

        {/* Operations list */}
        <div className="px-6 pt-4">
          <p className="text-xs text-text-secondary mb-2">
            Следующие изменения будут применены:
          </p>
        </div>
        <div
          ref={listRef}
          onScroll={handleScroll}
          className="mx-6 mb-4 max-h-56 overflow-y-auto rounded border border-border bg-bg-surface"
          tabIndex={0}
          aria-label="Список операций"
        >
          {operations.map((op, i) => {
            const kind = op.kind ?? 'custom'
            const badge = KIND_STYLES[kind]
            return (
              <div
                key={i}
                className="flex items-center gap-3 px-3 py-2 border-b border-border/50 last:border-0"
              >
                <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-medium ${badge}`}>
                  {KIND_LABELS[kind]}
                </span>
                <span className="text-sm text-text-primary">{op.label}</span>
              </div>
            )
          })}
        </div>

        {/* Acknowledge checkbox */}
        <div className="px-6 pb-4">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              id="review-gate-ack"
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="h-4 w-4 rounded accent-blue-500"
            />
            <span className="text-sm text-text-secondary">
              Я ознакомлен с перечнем изменений
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border bg-bg-surface">
          <button
            onClick={onCancel}
            className="rounded px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
            disabled={isLoading}
          >
            Отмена
          </button>
          <button
            id="review-gate-apply"
            onClick={onConfirm}
            disabled={!canApply}
            className={`rounded px-5 py-2 text-sm font-medium transition-all
              ${canApply
                ? 'bg-blue-600 text-white hover:bg-blue-500 active:scale-95'
                : 'bg-blue-600/30 text-white/40 cursor-not-allowed'
              }`}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                Применение…
              </span>
            ) : (
              'Применить'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ReviewGate
